import { useMemo, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Eye, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, Tag, Typography, Select, Table, Steps, Space, Descriptions, Button, Drawer } from '../../ui';
import type { TagTone } from '../../ui';
import { request } from '../../api/client';
import { TwDataTable as DataTable } from '../../components/TwDataTable';
import type { DataTableColumn } from '../../components/TwDataTable';
import { formatIst } from '../../lib/time';
import { MasterTag } from '../../lib/warehouseLabel';

const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const productText = (p: any, fallback?: any) =>
  p ? (p.variantLabel ? `${p.name} ${p.variantLabel}` : (p.sku ?? p.name)) : String(fallback ?? '');

const sourceTag = (v: string) => (v === 'TRANSFER'
  ? <Tag tone="cyan">Transfer</Tag>
  : v === 'PRODUCTION'
    ? <Tag tone="blue">Production (legacy)</Tag>
    : <Tag tone="neutral">Purchase</Tag>);
const grnStatusTag = (v: string) => <Tag tone={v === 'POSTED' ? 'green' : v === 'CANCELLED' ? 'red' : 'neutral'}>{v}</Tag>;

const TR_STATUS_COLOR: Record<string, TagTone> = {
  RECEIVED: 'green', PARTIAL: 'gold', IN_TRANSIT: 'blue', PREPARED: 'gold',
  SOURCE_ACK: 'cyan', REQUESTED: 'neutral', CANCELLED: 'red', REJECTED: 'red',
};

// Open a print-ready GRN document in a new tab; the browser's print dialog can save it as PDF.
function printGrn(grn: any, whLabel: (id: any) => any, productMap: Map<string, any>) {
  const esc = (s: any) => String(s ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));
  const origin = window.location.origin;
  const from = grn.fromWarehouseId ? whLabel(grn.fromWarehouseId) : (grn.vendorName ?? '—');
  const to = whLabel(grn.warehouseId);
  const lines = (grn.lines ?? []).map((l: any) => `
    <tr>
      <td>${esc(productText(productMap.get(String(l.productId)), l.productId))}</td>
      <td>${esc(l.batchNo)}</td><td>${esc(l.mfgDate ?? '')}</td><td>${esc(l.expDate ?? '')}</td>
      <td class="r">${Number(l.qty)}</td><td class="r">${esc(inr(l.value))}</td>
    </tr>`).join('');
  const timeline = grn.transfer ? `
    <div class="sec"><div class="sec-h">Status timeline · ${esc(grn.transfer.transferNo)} · ${esc(String(grn.transfer.status).replace(/_/g, ' '))}</div>
      <ol class="tl">${(grn.transfer.timeline ?? []).map((t: any) => `<li><b>${esc(t.label)}</b> — ${esc(formatIst(t.at))}${t.by ? ` · ${esc(t.by)}` : ''}</li>`).join('')}</ol></div>` : '';
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(grn.grnNo)}</title>
    <style>
      *{font-family:Arial,Helvetica,sans-serif;-webkit-print-color-adjust:exact;}
      body{margin:28px;color:#222;} h1{font-size:20px;margin:6px 0 2px;}
      .brand{color:#1F8A4C;font-weight:800;letter-spacing:.5px;} .muted{color:#777;font-size:12px;}
      .grid{display:grid;grid-template-columns:1fr 1fr;gap:6px 24px;margin:16px 0;font-size:13px;}
      .grid span{color:#777;display:inline-block;width:120px;}
      table{width:100%;border-collapse:collapse;margin-top:8px;font-size:12px;}
      th,td{border:1px solid #ddd;padding:6px 8px;text-align:left;} th{background:#f5f5f5;}
      .r{text-align:right;} .tot{font-weight:bold;}
      .sec{margin-top:18px;} .sec-h{font-weight:bold;font-size:13px;margin-bottom:6px;}
      .tl{margin:0;padding-left:18px;font-size:12px;line-height:1.7;}
    </style></head>
    <body onload="window.print()">
      <img src="${origin}/logo.png" alt="Green Bay Biotech International" style="height:64px;display:block;margin-bottom:6px;" />
      <h1>Goods Receipt Note</h1><div class="muted">${esc(grn.grnNo)}</div>
      <div class="grid">
        <div><span>Date</span>${esc(grn.grnDate)}</div><div><span>Status</span>${esc(grn.status)}</div>
        <div><span>From</span>${esc(from)}</div><div><span>To</span>${esc(to)}</div>
        <div><span>Source</span>${esc(grn.sourceType)}</div><div><span>Ref / Invoice</span>${esc(grn.vendorInvoiceNo ?? '—')}</div>
        <div><span>Value added</span><b>${esc(inr(grn.value))}</b></div>
      </div>
      ${timeline}
      <div class="sec"><div class="sec-h">Received items</div>
        <table><thead><tr><th>Product</th><th>Batch</th><th>Mfg</th><th>Exp</th><th class="r">Qty</th><th class="r">Value</th></tr></thead>
          <tbody>${lines}</tbody>
          <tfoot><tr><td colspan="5" class="r tot">Value added</td><td class="r tot">${esc(inr(grn.value))}</td></tr></tfoot>
        </table></div>
      <p class="muted" style="margin-top:24px;">Generated ${esc(formatIst(new Date().toISOString()))}</p>
    </body></html>`;
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(html);
  w.document.close();
}

export function GrnPage() {
  const [sourceType, setSourceType] = useState<string | undefined>();
  const [warehouseId, setWarehouseId] = useState<string | undefined>();
  const [productId, setProductId] = useState<string | undefined>();
  const [detailRow, setDetailRow] = useState<any | null>(null);

  const { data: warehouses } = useQuery({ queryKey: ['warehouses'], queryFn: () => request<any[]>({ url: '/warehouses' }) });
  const { data: products } = useQuery({ queryKey: ['products', 'all'], queryFn: () => request<any>({ url: '/products', params: { pageSize: 200 } }) });
  const productList = useMemo(() => products?.data ?? [], [products]);
  const whMap = useMemo(() => new Map<string, any>((warehouses ?? []).map((w: any) => [String(w.id), w])), [warehouses]);
  const productMap = useMemo(() => new Map<string, any>(productList.map((p: any) => [String(p.id), p])), [productList]);
  const whLabel = (id: any) => { const w = whMap.get(String(id)); return w ? `${w.code} · ${w.name}` : (id ? `#${id}` : '—'); };

  const params = useMemo(() => {
    const p: Record<string, string> = {};
    if (sourceType) p.sourceType = sourceType;
    if (warehouseId) p.warehouseId = warehouseId;
    if (productId) p.productId = productId;
    return p;
  }, [sourceType, warehouseId, productId]);

  const { data, isLoading } = useQuery({
    queryKey: ['grns', params],
    queryFn: () => request<any[]>({ url: '/grn', params }),
    placeholderData: keepPreviousData,
  });

  const columns: DataTableColumn<any>[] = [
    { key: 'grnNo', title: 'GRN No', dataIndex: 'grnNo', sortable: true, alwaysVisible: true },
    { key: 'grnDate', title: 'Date', dataIndex: 'grnDate', sortable: true },
    { key: 'sourceType', title: 'Source', dataIndex: 'sourceType', sortable: true, render: (v: string) => sourceTag(v) },
    // From: source warehouse for a transfer; for an external purchase, the vendor.
    { key: 'fromWarehouseId', title: 'From', dataIndex: 'fromWarehouseId', ellipsis: true, render: (v: any, r: any) => (v ? whLabel(v) : (r.vendorName ?? '—')) },
    // To: the receiving (destination) warehouse — always the GRN's own warehouse.
    { key: 'warehouseId', title: 'To', dataIndex: 'warehouseId', sortable: true, ellipsis: true, render: (v: any) => whLabel(v) },
    { key: 'value', title: 'Value added', dataIndex: 'value', sortable: true, align: 'right' as const, render: (v: any) => inr(v) },
    { key: 'vendorInvoiceNo', title: 'Ref / Invoice', dataIndex: 'vendorInvoiceNo' },
    { key: 'status', title: 'Status', dataIndex: 'status', sortable: true, render: (v: string) => grnStatusTag(v) },
    { key: 'vendorName', title: 'Vendor / Source', dataIndex: 'vendorName', sortable: true, ellipsis: true, defaultVisible: false },
    { key: 'createdAt', title: 'Created', dataIndex: 'createdAt', sortable: true, defaultVisible: false, render: (v: string) => formatIst(v) },
    {
      key: 'actions', title: '', alwaysVisible: true, width: 60, align: 'center' as const, fixed: 'right' as const,
      render: (_: any, r: any) => (
        <Button
          variant="text" size="sm" icon={<Eye size={16} />} title="View full details" aria-label="View full details"
          onClick={(e) => { e.stopPropagation(); setDetailRow(r); }}
        />
      ),
    },
  ];

  return (
    <div>
      <Typography.Title level={4}>Goods Receipt Notes (GRN)</Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
        GRNs are generated automatically when an inter-warehouse transfer is received — they are not created manually.
      </Typography.Paragraph>
      <Card>
        <DataTable
          storageKey="grn-list"
          rowKey="id"
          loading={isLoading}
          dataSource={data ?? []}
          columns={columns}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          expandable={{
            expandedRowRender: (r: any) => <GrnExpanded id={r.id} productMap={productMap} whLabel={whLabel} />,
          }}
          toolbarLeft={
            <>
              <Select
                placeholder="Source" className="w-37.5"
                value={sourceType} onChange={setSourceType}
                options={[{ value: 'TRANSFER', label: 'Transfer' }, { value: 'PURCHASE', label: 'Purchase' }, { value: 'PRODUCTION', label: 'Production (legacy)' }]}
              />
              <Select
                placeholder="Warehouse" className="w-50"
                value={warehouseId} onChange={setWarehouseId}
                options={(warehouses ?? []).map((w: any) => ({ value: String(w.id), label: <span className="inline-flex items-center">{`${w.code} · ${w.name}`}{w.isMaster ? <MasterTag /> : null}</span> }))}
              />
              <Select
                placeholder="Product" className="w-55"
                value={productId} onChange={setProductId}
                options={productList.map((p: any) => ({ value: String(p.id), label: `${productText(p)} · ${p.sku}` }))}
              />
            </>
          }
        />
      </Card>

      <GrnDetailDrawer row={detailRow} open={!!detailRow} onClose={() => setDetailRow(null)} productMap={productMap} whLabel={whLabel} />
    </div>
  );
}

// Status timeline for a transfer-sourced GRN: From → To, current status, and the lifecycle stamps
// (requested → acknowledged → prepared → dispatched → received) with the time + who did each.
function TransferTimeline({ transfer, whLabel }: { transfer: any; whLabel: (id: any) => any }) {
  const navigate = useNavigate();
  const STAGES = [
    { key: 'REQUESTED', label: 'Requested' },
    { key: 'SOURCE_ACK', label: 'Source acknowledged' },
    { key: 'PREPARED', label: 'Prepared' },
    { key: 'IN_TRANSIT', label: 'Dispatched' },
    { key: 'RECEIVED', label: 'Received' },
  ];
  const byKey = new Map<string, any>((transfer.timeline ?? []).map((t: any) => [t.key, t]));
  const lastDone = STAGES.reduce((acc, s, i) => (byKey.has(s.key) ? i : acc), -1);
  return (
    <div style={{ marginBottom: 16, padding: '4px 0' }}>
      <Space wrap style={{ marginBottom: 12 }}>
        <span
          className="cursor-pointer" title="Open this transfer"
          onClick={() => navigate(`/inventory/master-stock?tab=transfers&q=${encodeURIComponent(transfer.transferNo)}`)}
        >
          <Tag tone="cyan">{transfer.transferNo} ↗</Tag>
        </span>
        <span><b>{whLabel(transfer.fromWarehouseId)}</b> → <b>{whLabel(transfer.toWarehouseId)}</b></span>
        <Tag tone={TR_STATUS_COLOR[transfer.status] ?? 'neutral'}>{String(transfer.status).replace(/_/g, ' ')}</Tag>
      </Space>
      <Steps
        size="small"
        current={lastDone}
        items={STAGES.map((s) => {
          const t = byKey.get(s.key);
          return {
            title: s.label,
            description: t ? (
              <div style={{ fontSize: 11, lineHeight: 1.4 }}>
                <div>{formatIst(t.at)}</div>
                {t.by && <div style={{ color: '#999' }}>by {t.by}</div>}
              </div>
            ) : undefined,
          };
        })}
      />
    </div>
  );
}

// The received lines with their value + a "Value added" total — shared by the inline expand + the drawer.
function GrnLinesTable({ lines, value, loading, productMap }: { lines: any[]; value: any; loading?: boolean; productMap: Map<string, any> }) {
  return (
    <Table
      size="small"
      loading={loading}
      pagination={false}
      rowKey="id"
      dataSource={lines}
      columns={[
        { title: 'Product', render: (_: any, l: any) => productText(productMap.get(String(l.productId)), l.productId) },
        { title: 'Batch', dataIndex: 'batchNo' },
        { title: 'Mfg', dataIndex: 'mfgDate' },
        { title: 'Exp', dataIndex: 'expDate' },
        { title: 'Qty', dataIndex: 'qty', align: 'right' as const, render: (v: any) => Number(v) },
        { title: 'Value', dataIndex: 'value', align: 'right' as const, render: (v: any) => inr(v) },
      ]}
      footer={() => (
        <div className="flex items-center justify-end gap-6 font-semibold text-content">
          <span>Value added</span>
          <span>{inr(value)}</span>
        </div>
      )}
    />
  );
}

// Expanded GRN row: the status timeline (for transfer receipts) + the received lines with value.
function GrnExpanded({ id, productMap, whLabel }: { id: string; productMap: Map<string, any>; whLabel: (id: any) => any }) {
  const { data, isLoading } = useQuery({
    queryKey: ['grn', id],
    queryFn: () => request<any>({ url: `/grn/${id}` }),
  });
  return (
    <div>
      {data?.transfer && <TransferTimeline transfer={data.transfer} whLabel={whLabel} />}
      <GrnLinesTable lines={data?.lines ?? []} value={data?.value} loading={isLoading} productMap={productMap} />
    </div>
  );
}

// Full-detail drawer opened from the eye icon — header summary + status timeline + received items.
function GrnDetailDrawer({ row, open, onClose, productMap, whLabel }: {
  row: any | null; open: boolean; onClose: () => void; productMap: Map<string, any>; whLabel: (id: any) => any;
}) {
  const id = row?.id;
  const { data, isLoading } = useQuery({
    queryKey: ['grn', id],
    queryFn: () => request<any>({ url: `/grn/${id}` }),
    enabled: !!id && open,
  });
  return (
    <Drawer
      open={open} onClose={onClose} width={720} title={row ? `GRN · ${row.grnNo}` : 'GRN'}
      extra={<Button variant="ghost" icon={<Download size={16} />} disabled={!data} onClick={() => data && printGrn(data, whLabel, productMap)}>Download</Button>}
    >
      {row && (
        <>
          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label="GRN No">{row.grnNo}</Descriptions.Item>
            <Descriptions.Item label="Date">{row.grnDate}</Descriptions.Item>
            <Descriptions.Item label="Source">{sourceTag(row.sourceType)}</Descriptions.Item>
            <Descriptions.Item label="Status">{grnStatusTag(row.status)}</Descriptions.Item>
            <Descriptions.Item label="From">{row.fromWarehouseId ? whLabel(row.fromWarehouseId) : (row.vendorName ?? '—')}</Descriptions.Item>
            <Descriptions.Item label="To">{whLabel(row.warehouseId)}</Descriptions.Item>
            <Descriptions.Item label="Value added"><b style={{ color: '#1F8A4C' }}>{inr(data?.value ?? row.value)}</b></Descriptions.Item>
            <Descriptions.Item label="Ref / Invoice">{row.vendorInvoiceNo ?? '—'}</Descriptions.Item>
            {row.vendorName && <Descriptions.Item label="Vendor / Source" span={2}>{row.vendorName}</Descriptions.Item>}
            <Descriptions.Item label="Created" span={2}>{formatIst(row.createdAt)}</Descriptions.Item>
            {data?.remarks && <Descriptions.Item label="Remarks" span={2}>{data.remarks}</Descriptions.Item>}
          </Descriptions>

          {data?.transfer && (
            <div style={{ marginTop: 20 }}>
              <Typography.Text strong>Status timeline</Typography.Text>
              <div style={{ marginTop: 8 }}><TransferTimeline transfer={data.transfer} whLabel={whLabel} /></div>
            </div>
          )}

          <Typography.Text strong style={{ display: 'block', marginTop: 20, marginBottom: 8 }}>Received items</Typography.Text>
          <GrnLinesTable lines={data?.lines ?? []} value={data?.value} loading={isLoading} productMap={productMap} />
        </>
      )}
    </Drawer>
  );
}
